'use client'

import { useMemo } from 'react'
import type { SeatDetail } from '@/types/seating'
import { formatCurrencyAmount } from '@/lib/i18n/currency'
import { richTextToPlain } from '@/lib/sanitize-rich-text'

interface SeatTooltipProps {
  seat: SeatDetail | null
  sectionName?: string
  sectionDescription?: string | null
  sectionViewImageUrl?: string | null
  x: number
  y: number
  currency?: string
  locale?: string
  allInPrice?: number | null  // pre-computed all-in price (fees + tax included)
}

const TOOLTIP_WIDTH = 300
const TOOLTIP_HEIGHT = 240 // taller to account for view image

export default function SeatTooltip({ seat, sectionName, sectionDescription, sectionViewImageUrl, x, y, currency = 'USD', locale, allInPrice }: SeatTooltipProps) {
  // The hook comes FIRST, before the early return. `seat` is null whenever
  // nothing is hovered and this component is rendered unconditionally by both
  // select-seats and the embed, so a hook below the guard means a component
  // whose hook count changes with the mouse. React tolerates that today; it is
  // not a rule to rely on.
  const { left, top, flipBelow } = useMemo(() => {
    if (typeof window === 'undefined') {
      return { left: x, top: y - 12, flipBelow: false }
    }

    // Position tooltip directly above/below the seat center
    let calcLeft = x
    let calcTop = y - 12
    let below = false

    if (calcTop - TOOLTIP_HEIGHT < 8) {
      calcTop = y + 20
      below = true
    }

    if (below && calcTop + TOOLTIP_HEIGHT > window.innerHeight - 8) {
      calcTop = window.innerHeight - TOOLTIP_HEIGHT - 8
    }

    return { left: calcLeft, top: calcTop, flipBelow: below }
  }, [x, y])

  // Nothing hovered, nothing to draw — but BELOW the hook, so the hook count
  // stays the same on every render.
  if (!seat) return null

  const isAvailable = seat.status === 'FREE'

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        left,
        top,
        transform: flipBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
      }}
    >
      <div className="bg-surface-tertiary text-text-primary border border-surface-border rounded-xl shadow-xl backdrop-blur-sm max-w-[300px] overflow-hidden">
        {/* View from seat image */}
        {sectionViewImageUrl && (
          <img
            src={sectionViewImageUrl}
            alt={sectionName ? `View from ${sectionName}` : 'View from seat'}
            className="w-full h-24 object-cover"
          />
        )}

        {/* Category color bar */}
        {seat.category && !sectionViewImageUrl && (
          <div
            className="h-[3px] w-full"
            style={{ backgroundColor: seat.category.color }}
          />
        )}

        <div className="px-3.5 py-2.5">
          {/* Section / Row / Seat header */}
          <div className="text-xs font-semibold tracking-wider uppercase text-text-tertiary mb-0.5">
            {sectionName && <>{/^section\b/i.test(sectionName.trim()) ? sectionName : `Section ${sectionName}`} / </>}
            Row {seat.rowLabel} / Seat {seat.seatNumber}
          </div>

          {/* Section description (plain text — descriptions may carry HTML) */}
          {sectionDescription && (
            <p className="text-xs text-text-secondary mb-1.5 line-clamp-2 normal-case tracking-normal font-normal">
              {richTextToPlain(sectionDescription)}
            </p>
          )}

          {/* Category + Price row */}
          <div className="flex items-center justify-between gap-2">
            {seat.category && (
              <div className="flex items-center gap-1.5 text-sm">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: seat.category.color }}
                />
                <span className="text-text-secondary">{seat.category.name}</span>
              </div>
            )}
            {(allInPrice != null || seat.price != null) && (
              <span className="text-sm font-bold text-text-primary">
                {formatCurrencyAmount(allInPrice ?? seat.price ?? 0, currency, locale)}
              </span>
            )}
          </div>

          {/* Status */}
          <div className={`mt-1.5 text-xs font-medium ${isAvailable ? 'text-success' : 'text-text-tertiary'}`}>
            {isAvailable ? 'Available -- Click to select' : 'Unavailable'}
          </div>
        </div>
      </div>

      {/* Arrow centered on tooltip, pointing at the seat */}
      {!flipBelow && (
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-[5px]">
          <div className="w-2.5 h-2.5 rotate-45 bg-surface-tertiary border-r border-b border-surface-border" />
        </div>
      )}
      {flipBelow && (
        <div className="absolute left-1/2 -translate-x-1/2 -top-[5px]">
          <div className="w-2.5 h-2.5 rotate-45 bg-surface-tertiary border-l border-t border-surface-border" />
        </div>
      )}
    </div>
  )
}
